import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Check, Pencil, Plus, Search, Trash2 } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import type { DinTree } from "../../../administration/din276/din276Tree";
import {
  DIN276_CODE_PATTERN,
  EMPTY_CROSSWALK_FILTER,
  filterCrosswalkEntries,
  type ClassCrosswalkDraft,
  type ClassCrosswalkEntry,
  type CrosswalkFilter,
  type CrosswalkSource,
} from "./classCrosswalkAPI";

/**
 * "Kundenklassen": the customer's own asset classes and the DIN 276 node each stands for. Three digits
 * fix the cost group and let every asset choose its node inside it; five digits fix the node. Every row
 * shows what follows from it — the regulated object and how many obligations it carries — so a change
 * is seen with its effect. Entries derived by our pipeline are accepted one by one or as a selection;
 * an entry a person changed or accepted is never overwritten by an automatic run.
 */

interface Properties {
  tree: DinTree | null;
  entries: ClassCrosswalkEntry[];
  canWrite: boolean;
  onCreate: (draft: ClassCrosswalkDraft) => Promise<void>;
  onUpdate: (id: string, draft: ClassCrosswalkDraft, confirm: boolean) => Promise<void>;
  onConfirm: (ids: string[]) => Promise<number>;
  onDelete: (id: string) => Promise<void>;
}

const SOURCES: CrosswalkSource[] = ["derived", "manual", "confirmed"];

const emptyDraft = (scheme: string): ClassCrosswalkDraft => ({ scheme, classCode: "", din276Code: "" });

export default function ClassCrosswalkTab({ tree, entries, canWrite, onCreate, onUpdate, onConfirm, onDelete }: Properties) {
  const { t } = useTranslation();
  const fieldId = useId();
  const [filter, setFilter] = useState<CrosswalkFilter>(EMPTY_CROSSWALK_FILTER);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** The entry being edited, or a new draft (no `_id`). */
  const [editing, setEditing] = useState<(ClassCrosswalkDraft & { _id?: string }) | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ClassCrosswalkEntry | null>(null);

  const rows = useMemo(() => filterCrosswalkEntries(entries, filter), [entries, filter]);
  const defaultScheme = entries[0]?.scheme ?? "";

  useEffect(() => {
    const visible = new Set(rows.map((row) => row._id));
    setSelected((current) => new Set(Array.from(current).filter((id) => visible.has(id))));
  }, [rows]);

  const change = (patch: Partial<CrosswalkFilter>) => {
    setFilter((current) => ({ ...current, ...patch }));
    setPage(1);
  };
  const toggle = (id: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const confirmSelected = async (ids: string[]) => {
    try {
      const count = await onConfirm(ids);
      toast.success(t("operatorObligationsPanel.dataModel.crosswalk.confirmed", { count }));
      setSelected(new Set());
    } catch (error: any) {
      toast.error(error?.message ?? t("global.generalError"));
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    const entry = confirmDelete;
    setConfirmDelete(null);
    try {
      await onDelete(entry._id);
      toast.success(t("operatorObligationsPanel.dataModel.crosswalk.deleted", { code: entry.classCode }));
    } catch (error: any) {
      toast.error(error?.message ?? t("global.generalError"));
    }
  };

  const derivedSelected = Array.from(selected).filter((id) => entries.find((e) => e._id === id)?.source === "derived");
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row._id));

  const columns: ColumnDef<ClassCrosswalkEntry>[] = [
    ...(canWrite
      ? [
          {
            id: "select",
            size: 40,
            header: () => (
              <Checkbox
                checked={allSelected}
                aria-label={t("operatorObligationsPanel.dataModel.crosswalk.selectAll")}
                onCheckedChange={(checked) => setSelected(checked === true ? new Set(rows.map((row) => row._id)) : new Set())}
              />
            ),
            cell: ({ row }: { row: { original: ClassCrosswalkEntry } }) => (
              <Checkbox
                checked={selected.has(row.original._id)}
                aria-label={t("operatorObligationsPanel.dataModel.crosswalk.select", { code: row.original.classCode })}
                onCheckedChange={(checked) => toggle(row.original._id, checked === true)}
              />
            ),
          } as ColumnDef<ClassCrosswalkEntry>,
        ]
      : []),
    {
      id: "trade",
      header: t("operatorObligationsPanel.dataModel.crosswalk.trade"),
      accessorFn: (row) => [row.tradeCode, row.tradeLabel].filter(Boolean).join(" · "),
      size: 160,
    },
    {
      id: "class",
      header: t("operatorObligationsPanel.dataModel.crosswalk.class"),
      accessorFn: (row) => `${row.classCode} ${row.classLabel ?? ""}`,
      cell: ({ row }) => (
        <span>
          <span className="font-mono text-xs">{row.original.classCode}</span> {row.original.classLabel}
        </span>
      ),
      size: 240,
    },
    {
      id: "component",
      header: t("operatorObligationsPanel.dataModel.crosswalk.component"),
      accessorFn: (row) => `${row.componentCode ?? ""} ${row.componentLabel ?? ""}`,
      cell: ({ row }) =>
        row.original.componentCode ? (
          <span>
            <span className="font-mono text-xs">{row.original.componentCode}</span> {row.original.componentLabel}
          </span>
        ) : (
          <span className="text-muted-foreground">{t("operatorObligationsPanel.dataModel.crosswalk.allComponents")}</span>
        ),
      size: 200,
    },
    {
      id: "din276",
      header: t("operatorObligationsPanel.dataModel.crosswalk.din276"),
      accessorFn: (row) => `${row.din276Code} ${row.din276Label ?? ""}`,
      cell: ({ row }) => (
        <span title={row.original.note ?? undefined}>
          <span className="font-mono text-xs">{row.original.din276Code}</span>{" "}
          {row.original.din276Label ?? tree?.byCode[row.original.din276Code]?.label}
          {row.original.din276Code.length === 3 ? (
            <span className="ml-1 text-xs text-muted-foreground">{t("operatorObligationsPanel.dataModel.crosswalk.frame")}</span>
          ) : null}
        </span>
      ),
      size: 260,
    },
    {
      id: "object",
      header: t("operatorObligationsPanel.dataModel.crosswalk.object"),
      accessorFn: (row) => row.regulatedObjectName ?? "",
      cell: ({ row }) =>
        row.original.regulatedObjectName ? (
          <span>
            {row.original.regulatedObjectName}
            <span className="ml-1 text-xs text-muted-foreground">
              {t("operatorObligationsPanel.dataModel.crosswalk.obligations", { count: row.original.obligationCount ?? 0 })}
            </span>
          </span>
        ) : (
          <span className="text-muted-foreground">{t("operatorObligationsPanel.dataModel.crosswalk.noObject")}</span>
        ),
      size: 240,
    },
    {
      id: "assets",
      header: t("operatorObligationsPanel.dataModel.crosswalk.assets"),
      accessorFn: (row) => row.assetCount ?? 0,
      size: 80,
    },
    {
      id: "source",
      header: t("operatorObligationsPanel.dataModel.crosswalk.source"),
      accessorFn: (row) => row.source,
      cell: ({ row }) => (
        <Badge variant={row.original.source === "derived" ? "outline" : row.original.source === "confirmed" ? "default" : "secondary"}>
          {t(`operatorObligationsPanel.dataModel.crosswalk.sources.${row.original.source}`)}
        </Badge>
      ),
      size: 110,
    },
    ...(canWrite
      ? [
          {
            id: "actions",
            size: 120,
            header: "",
            cell: ({ row }: { row: { original: ClassCrosswalkEntry } }) => (
              <div className="flex">
                {row.original.source === "derived" ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    aria-label={t("operatorObligationsPanel.dataModel.crosswalk.confirmOne", { code: row.original.classCode })}
                    onClick={() => confirmSelected([row.original._id])}
                  >
                    <Check size={14} />
                  </Button>
                ) : null}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  aria-label={t("operatorObligationsPanel.dataModel.crosswalk.editOne", { code: row.original.classCode })}
                  onClick={() => setEditing({ ...row.original })}
                >
                  <Pencil size={14} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  aria-label={t("operatorObligationsPanel.dataModel.crosswalk.deleteOne", { code: row.original.classCode })}
                  onClick={() => setConfirmDelete(row.original)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ),
          } as ColumnDef<ClassCrosswalkEntry>,
        ]
      : []),
  ];

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="min-w-0 break-words text-sm text-muted-foreground">{t("operatorObligationsPanel.dataModel.crosswalk.intro")}</p>
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex w-[280px] max-w-full flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-text`}>{t("operatorObligationsPanel.dataModel.crosswalk.search")}</Label>
          <div className="relative">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input id={`${fieldId}-text`} type="search" className="pl-9" value={filter.text} onChange={(event) => change({ text: event.target.value })} />
          </div>
        </div>
        <div className="flex w-[180px] max-w-full flex-col gap-1.5">
          <Label htmlFor={`${fieldId}-source`}>{t("operatorObligationsPanel.dataModel.crosswalk.source")}</Label>
          <Select value={filter.source} onValueChange={(value) => change({ source: value as CrosswalkFilter["source"] })}>
            <SelectTrigger id={`${fieldId}-source`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("operatorObligationsPanel.dataModel.crosswalk.allSources")}</SelectItem>
              {SOURCES.map((source) => (
                <SelectItem key={source} value={source}>
                  {t(`operatorObligationsPanel.dataModel.crosswalk.sources.${source}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">{t("operatorObligationsPanel.dataModel.crosswalk.count", { count: rows.length })}</span>
        {canWrite ? (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" disabled={derivedSelected.length === 0} onClick={() => confirmSelected(derivedSelected)}>
              <Check size={14} aria-hidden />
              {t("operatorObligationsPanel.dataModel.crosswalk.confirmSelected", { count: derivedSelected.length })}
            </Button>
            <Button size="sm" className="gap-1.5" onClick={() => setEditing(emptyDraft(defaultScheme))}>
              <Plus size={14} aria-hidden />
              {t("operatorObligationsPanel.dataModel.crosswalk.add")}
            </Button>
          </div>
        ) : null}
      </div>
      <div className="min-w-0 overflow-x-auto">
        <DataTable
          columns={columns}
          data={rows}
          pageSize={20}
          page={page}
          onPageChange={setPage}
          emptyMessage={
            entries.length === 0
              ? t("operatorObligationsPanel.dataModel.crosswalk.noneYet")
              : t("operatorObligationsPanel.dataModel.crosswalk.none")
          }
          previousAriaLabel={t("operatorResponsibilityPanel.previousPage")}
          nextAriaLabel={t("operatorResponsibilityPanel.nextPage")}
        />
      </div>
      <CrosswalkEntryDialog
        tree={tree}
        draft={editing}
        onDismiss={() => setEditing(null)}
        onSave={async (draft, confirm) => {
          if (editing?._id) await onUpdate(editing._id, draft, confirm);
          else await onCreate(draft);
          toast.success(t("operatorObligationsPanel.dataModel.crosswalk.saved", { code: draft.classCode }));
          setEditing(null);
        }}
      />
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("operatorObligationsPanel.dataModel.crosswalk.deleteTitle", { code: confirmDelete?.classCode ?? "" })}</AlertDialogTitle>
            <AlertDialogDescription>{t("operatorObligationsPanel.dataModel.crosswalk.deleteDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={remove}>{t("operatorObligationsPanel.dataModel.crosswalk.deleteConfirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Create or change one entry; the DIN 276 code is checked against the rule and the tree before saving. */
function CrosswalkEntryDialog({
  tree,
  draft,
  onDismiss,
  onSave,
}: {
  tree: DinTree | null;
  draft: (ClassCrosswalkDraft & { _id?: string; source?: CrosswalkSource }) | null;
  onDismiss: () => void;
  onSave: (draft: ClassCrosswalkDraft, confirm: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const fieldId = useId();
  const [value, setValue] = useState<ClassCrosswalkDraft>(draft ?? emptyDraft(""));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!draft) return;
    const { scheme, tradeCode, tradeLabel, classCode, classLabel, componentCode, componentLabel, din276Code, note } = draft;
    setValue({ scheme, tradeCode, tradeLabel, classCode, classLabel, componentCode, componentLabel, din276Code, note });
    setError(null);
    setSaving(false);
  }, [draft]);

  const isNew = !draft?._id;
  const din276Label = tree?.byCode[value.din276Code.trim()]?.label;
  const set = (patch: Partial<ClassCrosswalkDraft>) => setValue((current) => ({ ...current, ...patch }));

  const save = async (confirm: boolean) => {
    const din276Code = value.din276Code.trim();
    if (!value.scheme.trim() || !value.classCode.trim()) {
      setError(t("operatorObligationsPanel.dataModel.crosswalk.required"));
      return;
    }
    if (!DIN276_CODE_PATTERN.test(din276Code) || (tree && !tree.byCode[din276Code])) {
      setError(t("operatorObligationsPanel.dataModel.crosswalk.invalidCode", { code: din276Code }));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ ...value, din276Code }, confirm);
    } catch (saveError: any) {
      setError(saveError?.message ?? t("global.generalError"));
      setSaving(false);
    }
  };

  const field = (name: keyof ClassCrosswalkDraft, label: string, props: { disabled?: boolean; placeholder?: string } = {}) => (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={`${fieldId}-${name}`}>{label}</Label>
      <Input id={`${fieldId}-${name}`} value={(value[name] as string) ?? ""} onChange={(event) => set({ [name]: event.target.value })} {...props} />
    </div>
  );

  return (
    <Dialog open={!!draft} onOpenChange={(next) => !next && !saving && onDismiss()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? t("operatorObligationsPanel.dataModel.crosswalk.addTitle")
              : t("operatorObligationsPanel.dataModel.crosswalk.editTitle", { code: draft?.classCode ?? "" })}
          </DialogTitle>
          <DialogDescription>{t("operatorObligationsPanel.dataModel.crosswalk.dialogDescription")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {field("scheme", t("operatorObligationsPanel.dataModel.crosswalk.scheme"), { disabled: !isNew })}
          <div />
          {field("tradeCode", t("operatorObligationsPanel.dataModel.crosswalk.tradeCode"), { disabled: !isNew })}
          {field("tradeLabel", t("operatorObligationsPanel.dataModel.crosswalk.tradeLabel"))}
          {field("classCode", t("operatorObligationsPanel.dataModel.crosswalk.classCode"), { disabled: !isNew })}
          {field("classLabel", t("operatorObligationsPanel.dataModel.crosswalk.classLabel"))}
          {field("componentCode", t("operatorObligationsPanel.dataModel.crosswalk.componentCode"), { disabled: !isNew })}
          {field("componentLabel", t("operatorObligationsPanel.dataModel.crosswalk.componentLabel"))}
          <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
            {field("din276Code", t("operatorObligationsPanel.dataModel.crosswalk.din276Code"), { placeholder: "412 / 412.10 / 412.171" })}
            <span className="min-h-[1.25rem] text-xs text-muted-foreground">
              {din276Label
                ? `${din276Label} — ${value.din276Code.trim().length === 3 ? t("operatorObligationsPanel.dataModel.crosswalk.frameHint") : t("operatorObligationsPanel.dataModel.crosswalk.fixedHint")}`
                : null}
            </span>
          </div>
          <div className="sm:col-span-2">{field("note", t("operatorObligationsPanel.dataModel.crosswalk.note"))}</div>
        </div>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription className="min-w-0 break-words">{error}</AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={onDismiss}>
            {t("global.cancel")}
          </Button>
          {!isNew ? (
            <Button variant="outline" disabled={saving} onClick={() => save(true)}>
              {t("operatorObligationsPanel.dataModel.crosswalk.saveAndConfirm")}
            </Button>
          ) : null}
          <Button disabled={saving} onClick={() => save(false)}>
            {t("operatorObligationsPanel.dataModel.crosswalk.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
